import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Breadcrumb } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import { useLangPath } from "./hooks/useLangPath";
import { SUPPORTED_LANGS } from "./i18n";
import articlesData from "./articles/articles.json";
import Seo from "./Seo";

function ArticlePage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const langPath = useLangPath();
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Daily recaps are generated nightly as static assets and are NOT in the
  // bundled articles.json — the slug itself carries everything needed to
  // locate the files, so no index fetch (and no database read) is required.
  // useMemo matters here: meta is a useEffect dependency below, and a fresh
  // object every render would refetch the markdown in a loop.
  const meta = React.useMemo(() => {
    const recapDate = (slug.match(/^daily-market-recap-(\d{4}-\d{2}-\d{2})$/) || [])[1] || null;
    return recapDate
      ? { slug, file: `recaps/${slug}.md`, date: recapDate, recap: true,
          title: `Daily Market Recap — ${recapDate}`,
          header: "AI news-sentiment recap across forex, metals, indices, crypto and commodities." }
      : articlesData.find((a) => a.slug === slug);
  }, [slug]);
  const articleIndex = articlesData.findIndex((a) => a.slug === slug) + 1;
  const lang = i18n.language;

  useEffect(() => {
    if (!meta) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    const langFile =
      SUPPORTED_LANGS.includes(lang)
        ? meta.file.replace(/\.md$/, `.${lang}.md`)
        : meta.file;

    const fetchWithFallback = (primary, fallback) =>
      fetch(`/articles/${primary}`)
        .then((res) => {
          if (!res.ok) throw new Error("not found");
          return res.text();
        })
        .catch(() =>
          fetch(`/articles/${fallback}`).then((res) => {
            if (!res.ok) throw new Error("not found");
            return res.text();
          })
        );

    fetchWithFallback(langFile, meta.file)
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [slug, meta, lang]);

  if (loading) return <Container style={{ padding: "40px 0" }}>{t("common.loading")}</Container>;
  if (notFound) return <Container style={{ padding: "40px 0" }}>{t("common.articleNotFound")}</Container>;

  const translatedTitle = meta.recap
    ? t("articles.recapTitle", { date: meta.date, defaultValue: meta.title })
    : t(`articles.a${articleIndex}title`, { defaultValue: meta.title });
  const translatedHeader = meta.recap
    ? t("articles.recapHeader", { defaultValue: meta.header })
    : t(`articles.a${articleIndex}header`, { defaultValue: meta.header });

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: translatedTitle,
    description: translatedHeader,
    datePublished: meta.date,
    publisher: {
      "@type": "Organization",
      name: "A.I. Trading Predictions",
      url: "https://aitradingpredictions.com",
    },
  };

  return (
    <Container style={{ maxWidth: 760, padding: "32px 16px 80px" }}>
      <Seo
        title={`${translatedTitle} — A.I. Trading Predictions`}
        description={translatedHeader}
        canonical={`/articles/${slug}`}
        type="article"
        schema={articleSchema}
        breadcrumbs={[
          { name: t("common.home"), path: "/" },
          { name: t("articles.h1"), path: "/articles" },
          { name: translatedTitle, path: `/articles/${slug}` },
        ]}
      />
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: langPath("/") }}>{t("common.home")}</Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: langPath("/articles") }}>{t("articles.h1")}</Breadcrumb.Item>
        <Breadcrumb.Item active>{translatedTitle}</Breadcrumb.Item>
      </Breadcrumb>
      <p style={{ color: "var(--text-secondary)", fontSize: 14, marginBottom: 8, textAlign: "left" }}>{meta.date}</p>
      <div style={styles.prose}>
        <ReactMarkdown
          components={{
            img: ({ src, alt }) => (
              <img src={src} alt={alt || ""} loading="lazy"
                   style={{ maxWidth: "100%", height: "auto", borderRadius: 10,
                            border: "1px solid var(--surface-border, #233047)" }} />
            ),
            a: ({ href, children }) => {
              const isInternal = href && (href.startsWith("/") || href.startsWith("#"));
              return isInternal ? (
                <Link to={langPath(href)}>{children}</Link>
              ) : (
                <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
              );
            },
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    </Container>
  );
}

const styles = {
  prose: { lineHeight: 1.8, fontSize: 16, color: "var(--text)", textAlign: "left" },
};

export default ArticlePage;
